import { generatePath, useHistory } from 'react-router-dom'
import { useNetworkHomeJobsRouteParams } from 'features/networkAi/hooks'
import { useCallback, useState } from 'react'
import { SiteWlan } from 'features/networkAi/types'
import ROUTES from 'app/routes'
import { useIntl } from 'react-intl'
import {
  WLANListRow,
  WLANListItemBody,
  WLANListIndicator,
  WLANListLeftBlock,
  WLANListLeftBlockPrimary,
  WLANListLeftBlockStatus,
  WLANSeparator,
  WLANNameHightlighted,
} from './styles'
import { mdiWifiCheck, mdiWifiOff } from '@mdi/js'
import theme from 'theme_'
import {
  createSiteWlanThunk,
  deleteWLANThunk,
  enableWLANThunk,
  deleteSiteSsidPSKsThunk,
} from 'features/networkAi/slice'
import { useAppDispatch } from 'app/hooks'
import ConfirmModal from 'components/Modal/ConfirmModal'
import { networkWLANsMessages } from '../../messages'
import { commonMessages } from 'app/i18n/messages'
import { KebabMenu, KebabMenuItem } from 'components/KebabMenu'

interface WLANsListItemProps {
  siteId: string
  wlan: SiteWlan
}

/*
function formatAPNames(ap_id: string) {
    if (ap_id!==null){
        return ap_id
    } else {
        return 'All'
    } 
}
*/

function cleanVLAN(vlanids: string) {
  if (vlanids !== null && vlanids !== undefined) {
    return vlanids.toString().replace('{{', '').replace('}}', '')
  } else {
    return vlanids
  }
}

function formatVLAN(wlan: SiteWlan) {
  if (wlan.vlan_pooling === true) {
    return cleanVLAN(wlan.vlan_ids)
  } else {
    return cleanVLAN(wlan.vlan_id)
  }
}

function formatBands(bands: string[]) {
  let band = ''
  bands.forEach(function (value) {
    if (value === '24') {
      value = '2.4'
    }
    band += value + 'GHz / '
  })
  band = band.substring(0, band.length - 2)
  return band
}

function formatSecurity(wlan: SiteWlan) {
  if (wlan.auth.multi_psk_only) {
    return 'WPA-2/PSK (Multiple)'
  } else if (wlan.auth.type === 'open') {
    return 'Open'
  } else if (wlan.bands.toString().indexOf('6') !== -1) {
    return 'WPA3/SAE (+WPA2)'
  } else {
    return 'WPA-2/PSK'
  }
}

function formatGuest(enabled: boolean) {
  if (enabled) {
    return 'Yes'
  } else {
    return 'No'
  }
}

function WLANsListItem({ wlan, siteId }: WLANsListItemProps) {
  const dispatch = useAppDispatch()
  const { formatMessage } = useIntl()
  const [isHover, setIsHover] = useState(false)
  const history = useHistory()
  const { rootDomainId, homeId, jobId } = useNetworkHomeJobsRouteParams()
  const wlanId = wlan.id

  const [isDeleteWLANModalOpen, setIsDeleteWLANModalOpen] = useState(false)
  const [isDisableWLANModalOpen, setIsDisableWLANModalOpen] = useState(false)

  const handleConfirmToDeleteWLAN = useCallback(() => {
    setIsDeleteWLANModalOpen(true)
  }, [setIsDeleteWLANModalOpen])

  const handleConfirmToDisableWLAN = useCallback(() => {
    setIsDisableWLANModalOpen(true)
  }, [setIsDisableWLANModalOpen])

  const handleConfirmDeleteWLAN = useCallback(() => {
    dispatch(deleteWLANThunk(wlan.site_id, wlan.id))
    dispatch(deleteSiteSsidPSKsThunk(wlan.site_id, wlan.ssid))
    setIsDeleteWLANModalOpen(false)
  }, [dispatch, wlan, setIsDeleteWLANModalOpen])

  const handleConfirmDisableWLAN = useCallback(() => {
    const enableWLANBody = {
      enabled: !wlan.enabled,
      id: wlan.id,
    }

    dispatch(enableWLANThunk(siteId, enableWLANBody))

    setIsDisableWLANModalOpen(false)
  }, [dispatch, wlan, siteId, setIsDisableWLANModalOpen])

  const handleWLANCopy = async () => {
    let copyWlan = JSON.parse(JSON.stringify(wlan))
    copyWlan.ssid = wlan.ssid + '_Copy'
    await dispatch(createSiteWlanThunk(siteId, copyWlan))
  }

  const handleNavigateWlanEditClick = () => {
    const pathname = generatePath(ROUTES.networkAi.dashboard.editCreateWLANs, {
      rootDomainId,
      jobId,
      homeId,
      siteId,
      wlanId,
    })
    history.push(pathname)
  }

  return (
    <div id={wlan.id}>
      <ConfirmModal
        isOpen={isDeleteWLANModalOpen}
        title={formatMessage(networkWLANsMessages.titleDelete)}
        confirmBtnTitle={formatMessage(
          networkWLANsMessages.confirmDeleteMessage
        )}
        discardBtnTitle={formatMessage(commonMessages.cancel)}
        variant="primary"
        onConfirm={handleConfirmDeleteWLAN}
        onDiscard={() => setIsDeleteWLANModalOpen(false)}
      >
        {formatMessage(commonMessages.promptConfirm)}
        {formatMessage(networkWLANsMessages.titleDelete)}{' '}
        <WLANNameHightlighted>{wlan.ssid}</WLANNameHightlighted>?
      </ConfirmModal>
      <ConfirmModal
        isOpen={isDisableWLANModalOpen}
        title={
          wlan.enabled
            ? formatMessage(networkWLANsMessages.titleDisable)
            : formatMessage(networkWLANsMessages.titleEnable)
        }
        confirmBtnTitle={
          wlan.enabled
            ? formatMessage(networkWLANsMessages.confirmDisableMessage)
            : formatMessage(networkWLANsMessages.confirmEnableMessage)
        }
        discardBtnTitle={formatMessage(commonMessages.cancel)}
        variant="primary"
        onConfirm={handleConfirmDisableWLAN}
        onDiscard={() => setIsDisableWLANModalOpen(false)}
      >
        {formatMessage(commonMessages.promptConfirm)}
        {wlan.enabled
          ? formatMessage(networkWLANsMessages.titleDisable)
          : formatMessage(networkWLANsMessages.titleEnable)}{' '}
        <WLANNameHightlighted>{wlan.ssid}</WLANNameHightlighted>?
      </ConfirmModal>
      <WLANSeparator />
      <WLANListRow
        hover={isHover}
        onMouseEnter={() => setIsHover(true)}
        onMouseLeave={() => setIsHover(false)}
      >
        <WLANListLeftBlockStatus onClick={handleNavigateWlanEditClick}>
          <WLANListIndicator
            path={wlan.enabled ? mdiWifiCheck : mdiWifiOff}
            size={1.5}
            color={
              wlan.enabled ? theme.colors.successGreen : theme.colors.moreGrey
            }
          />
        </WLANListLeftBlockStatus>

        <WLANListLeftBlockPrimary onClick={handleNavigateWlanEditClick}>
          <WLANListItemBody>{wlan.ssid}</WLANListItemBody>
        </WLANListLeftBlockPrimary>

        <WLANListLeftBlockStatus />
        <WLANListLeftBlockStatus />

        <WLANListLeftBlock onClick={handleNavigateWlanEditClick}>
          <WLANListItemBody>{formatVLAN(wlan)}</WLANListItemBody>
        </WLANListLeftBlock>

        <WLANListLeftBlock onClick={handleNavigateWlanEditClick}>
          <WLANListItemBody>{formatBands(wlan.bands)}</WLANListItemBody>
        </WLANListLeftBlock>

        <WLANListLeftBlock onClick={handleNavigateWlanEditClick}>
          <WLANListItemBody>{formatSecurity(wlan)}</WLANListItemBody>
        </WLANListLeftBlock>

        <WLANListLeftBlock onClick={handleNavigateWlanEditClick}>
          <WLANListItemBody>
            {formatGuest(wlan.portal.enabled)}
          </WLANListItemBody>
        </WLANListLeftBlock>

        <KebabMenu extraStyles={{ zIndex: '2' }}>
          <KebabMenuItem
            title={formatMessage(networkWLANsMessages.copyWLAN)}
            onClick={handleWLANCopy}
          />
          <KebabMenuItem
            title={
              wlan.enabled
                ? formatMessage(networkWLANsMessages.titleDisable)
                : formatMessage(networkWLANsMessages.titleEnable)
            }
            onClick={handleConfirmToDisableWLAN}
          />
          <KebabMenuItem
            title={formatMessage(networkWLANsMessages.titleDelete)}
            onClick={handleConfirmToDeleteWLAN}
          />
        </KebabMenu>
      </WLANListRow>
    </div>
  )
}

export default WLANsListItem
